import { useEffect, useId, useRef, useState } from 'react'
import { useCompanion } from '@/hooks/useCompanion'
import { getCompanionWhy, type CompanionAnswer, type CompanionMeter } from '@/lib/companion'
import { CompanionEvidence } from '@/components/CompanionEvidence'

/** Mount only on an explicit Why? tap. Native dialog supplies focus trap, Escape
 * dismissal, background inertness and focus restoration without a new UI library. */
export function WhyTapModal({ meter, personId, onClose }: {
  meter: CompanionMeter; personId: string; onClose: () => void
}) {
  const { scope, enabled } = useCompanion()
  const dialog = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  const [result, setResult] = useState<{ key: string; answer: CompanionAnswer | null } | null>(null)
  const key = JSON.stringify([scope, personId, meter])
  useEffect(() => {
    const node = dialog.current
    if (enabled && scope?.personId === personId) node?.showModal()
    return () => { node?.close() }
  }, [enabled, scope, personId])
  useEffect(() => {
    let active = true
    if (enabled && scope?.personId === personId) {
      void getCompanionWhy(scope, meter).then((answer) => { if (active) setResult({ key, answer }) })
    }
    return () => { active = false }
    // key includes the entire meter/context without refetching on object-identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key])
  if (!enabled || !scope || scope.personId !== personId) return null
  const answer = result?.key === key ? result.answer : null
  return (
    <dialog ref={dialog} aria-labelledby={titleId} onCancel={onClose}
      onClick={(event) => { if (event.target === event.currentTarget) onClose() }}
      className="w-[calc(100%-2rem)] max-w-lg max-h-[85dvh] overflow-y-auto rounded-r-sm bg-bg-elevated text-text-primary border border-divider-subtle p-0 backdrop:bg-black/60">
      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-3">
          <h2 id={titleId} className="text-title">Why this {meter.label.toLowerCase()} meter?</h2>
          <button type="button" autoFocus onClick={onClose} className="min-h-11 px-3 underline">Close</button>
        </div>
        <p className="text-body">{answer?.text ?? (result?.key === key ? 'Context is unavailable or access has changed.' : 'Checking the contributing records…')}</p>
        {answer && <CompanionEvidence answer={answer} />}
        <p className="text-caption text-text-secondary">An explanation of recorded observations, not medical advice or a statement that care is safe.</p>
      </div>
    </dialog>
  )
}
